import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import { useUpdatePageMutation } from '@/services/CmsService';
import {
  formatLegalEffectiveDate,
  isValidLegalEffectiveDateInput,
  toLegalEffectiveDateInputValue,
} from '@/utils/legalEffectiveDate';

interface LegalEffectiveDateFieldProps {
  pageId: string | null;
  legalEffectiveFrom?: string | null;
  canUpdate: boolean;
}

export const LegalEffectiveDateField = ({
  pageId,
  legalEffectiveFrom,
  canUpdate,
}: LegalEffectiveDateFieldProps) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [updatePage, { isLoading }] = useUpdatePageMutation();
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(toLegalEffectiveDateInputValue(legalEffectiveFrom));
  }, [legalEffectiveFrom]);

  const handleSave = async () => {
    if (!pageId) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_PAGE_MISSING' }),
        { variant: 'error' }
      );
      return;
    }

    if (!isValidLegalEffectiveDateInput(value)) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_INVALID' }),
        { variant: 'error' }
      );
      return;
    }

    try {
      await updatePage({
        pageId,
        body: { legal_effective_from: value },
      }).unwrap();
      enqueueSnackbar(
        intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_SAVED' }),
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar(
        intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_SAVE_FAILED' }),
        { variant: 'error' }
      );
    }
  };

  return (
    <div className="mb-6 rounded-t-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex-1">
          <label htmlFor="legal-effective-from" className="form-label">
            {intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_LABEL' })}
          </label>
          <p className="mb-2 text-sm text-gray-600">
            {intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_HELP' })}
          </p>
          {canUpdate ? (
            <input
              id="legal-effective-from"
              type="date"
              className="input input-sm h-10 w-full max-w-xs"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              aria-describedby="legal-effective-from-current"
            />
          ) : (
            <p
              id="legal-effective-from-current"
              className="text-sm font-medium text-gray-900"
            >
              {formatLegalEffectiveDate(legalEffectiveFrom)}
            </p>
          )}
        </div>

        {canUpdate && (
          <button
            type="button"
            className={clsx(
              'btn btn-sm btn-primary flex items-center gap-2',
              (isLoading || !pageId) && 'btn-disabled opacity-50 cursor-not-allowed'
            )}
            onClick={handleSave}
            disabled={isLoading || !pageId}
            aria-label={intl.formatMessage({
              id: 'CMS.LEGAL_EFFECTIVE_DATE_SAVE',
            })}
          >
            <KeenIcon type="solid" icon="check-circle" className="text-white" />
            {intl.formatMessage({ id: 'CMS.LEGAL_EFFECTIVE_DATE_SAVE' })}
          </button>
        )}
      </div>
    </div>
  );
};
